/*
 * SLIDEIN COMPONENT
 *
 * RENDERS A SLIDE-OUT PANEL FOR COURSE RECOMMENDATIONS WITH A TOGGLE HANDLE.
 * MANAGES BODY CLASS FOR CHAT WINDOW BORDER RADIUS AND AUTO-SCROLLS CONTENT ON OPEN/UPDATE.
 */

import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./Slidein.module.scss";
import CourseRecommendation from "../Course_recomendation/CourseRecommendation";

/**
 * @param {Object} props
 * @param {Array<Object>} props.recommendations – Array of course recommendation data.
 * @param {boolean} props.isOpen – Whether the slide-in panel is currently open.
 * @param {function(boolean): void} props.setIsOpen – Setter to toggle panel open state.
 * @param {boolean} props.closing – Whether the parent chat window is in its closing animation.
 */
const Slidein = ({
  recommendations = [],
  isOpen,
  setIsOpen,
  closing,
  host,
}) => {
  const contentRef = useRef(null);

  /**
   * Toggles the slide-in panel open/closed.
   * Notify parent window.
   */
  const toggleSlide = () => {
    const next = !isOpen;
    setIsOpen(next);
    window.parent.postMessage({ type: "chat:slidein", open: next }, "*");
  };

  /**
   * Synchronize the `slidein-open` body class to control chat-window corner radius.
   */
  useEffect(() => {
    document.body.classList.toggle("slidein-open", isOpen);
  }, [isOpen]);

  // new: tell the parent iframe to expand/contract
  useEffect(() => {
    window.parent.postMessage({ type: "chat:slidein", open: isOpen }, "*");
  }, [isOpen]);

  /**
   * Auto-scroll the recommendation list to the bottom when opened or updated.
   */
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [isOpen, recommendations]);

  return (
    <div
      className={[
        styles.slideinContainer,
        isOpen ? styles.open : "",
        closing ? styles.closing : "",
      ].join(" ")}
    >
      {/* Toggle handle for opening/closing */}
      <div className={styles.toggleButton} onClick={toggleSlide}>
        <img
          src={isOpen ? "/assets/right_arrow.svg" : "/assets/left_arrow.svg"}
          alt="Toggle arrow"
        />
      </div>

      {/* Panel content with recommendations or placeholder */}
      <div
        className={`${styles.slideinContent} ${
          isOpen ? styles.showContent : ""
        }`}
        ref={contentRef}
      >
        {recommendations.length === 0 ? (
          <p>
            När du frågar om utbildning så kommer här att visas
            rekommendationer. Du kan klicka på rekommendationen och läsa vidare
            om utbildningen på vår hemsida.
          </p>
        ) : (
          recommendations.map((rec) => (
            <CourseRecommendation key={rec.courseId} data={rec} host={host} />
          ))
        )}
      </div>
    </div>
  );
};

Slidein.propTypes = {
  recommendations: PropTypes.arrayOf(PropTypes.object),
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  closing: PropTypes.bool,
  host: PropTypes.string,
};

Slidein.defaultProps = {
  recommendations: [],
  closing: false,
};

export default Slidein;
